import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { GitBranch, Trash2 } from 'lucide-react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@design-system';
import { useConditionBlock, useConditionSide } from '../../hooks/use-reply-nodes';

/** "This part changes depending on whether …": the condition, then its two sides side by side. */
export function ConditionView(props: NodeViewProps) {
  const block = useConditionBlock(props);

  return (
    <NodeViewWrapper className="my-3 flex flex-col gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3.5">
      <div
        contentEditable={false}
        className="flex flex-wrap items-center gap-2.5"
      >
        <GitBranch
          className="size-[18px] text-zinc-600"
          aria-hidden
        />
        <span className="text-sm font-semibold text-zinc-700">
          This part changes depending on whether
        </span>
        <Select
          value={block.flag}
          onValueChange={block.setFlag}
        >
          <SelectTrigger
            className="h-10 w-auto min-w-56 bg-white"
            aria-label="Condition"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {block.conditions.map(condition => (
              <SelectItem
                key={condition.name}
                value={condition.name}
              >
                {condition.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto text-zinc-600"
          onClick={block.remove}
        >
          <Trash2 aria-hidden /> Remove
        </Button>
      </div>
      <NodeViewContent className="[&>div]:grid [&>div]:grid-cols-1 [&>div]:gap-3 md:[&>div]:grid-cols-2" />
    </NodeViewWrapper>
  );
}

export function ConditionSideView(props: NodeViewProps) {
  const side = useConditionSide(props);

  return (
    <NodeViewWrapper className="flex min-w-0 flex-col gap-1.5">
      <span
        contentEditable={false}
        className={cn(
          'text-[13px] font-semibold',
          side.side === 'then' ? 'text-[#1B6B3A]' : 'text-[#8A4B00]',
        )}
      >
        {side.title}
      </span>
      <NodeViewContent className="min-h-[104px] rounded-[10px] border border-zinc-300 bg-white px-3.5 py-3 text-[15px] leading-[1.9] focus-within:border-[#5B3FD6] focus-within:ring-2 focus-within:ring-[#5B3FD6]/15" />
    </NodeViewWrapper>
  );
}
